import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import JobDetailsAccordion from "./JobDetailsAccordion";
import { API_BASE_URL } from "../../../config/api"; // organism -> component -> details -> pages -> config
import { notifications } from "@mantine/notifications";
import { IconX } from "@tabler/icons-react";

// ---------- helpers ----------
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function formatDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

// normalize backend response -> UI fields we use
function mapJobResponse(raw) {
  if (!raw) return null;
  return {
    id: raw.id ?? raw._id ?? raw.jobId,
    referenceNumber: raw.referenceNumber ?? raw.requestId ?? "",
    positionTitle: raw.positionTitle ?? raw.title ?? "Job Details",
    department: raw.department ?? raw.organisationName ?? raw.organization ?? "",
    level: raw.mtnLevel ?? raw.jobLevel ?? "",
    region: raw.region ?? "",
    city: raw.city ?? "",
    createdAt: raw.createdAt ?? raw.created_on ?? "",
    applicationDeadline: raw.applicationDeadline ?? raw.deadline ?? null,

    // rich text
    aboutMtn: raw.aboutMtn ?? raw.about ?? "",
    mission: raw.mission ?? "",
    keyPerformanceAreas: raw.keyPerformanceAreas ?? raw.kpa ?? "",
    experience: raw.experience ?? "",
    educationBackground: raw.educationBackground ?? raw.education ?? "",
    applyInstructions: raw.applyInstructions ?? raw.howToApply ?? "",
  };
}

const MetaItem = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-[11px] uppercase tracking-wide text-gray-500">{label}</span>
    <span className="text-sm font-medium text-gray-900">{value || "-"}</span>
  </div>
);

const JobDetailsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const axiosCfg = useMemo(() => ({ headers: authHeaders() }), []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE_URL}/api/jobs/${jobId}`, axiosCfg);
        const payload = res?.data?.data ?? res?.data ?? null;
        const mapped = mapJobResponse(payload);

        if (!cancelled) {
          if (!mapped) {
            notifications.show({
              title: "Job not found",
              message: "We could not load this vacancy.",
              color: "red",
              icon: <IconX size={20} />,
            });
            navigate("/career");
            return;
          }
          setJob(mapped);
        }
      } catch (err) {
        console.error("Failed to load job details:", err?.response?.data || err?.message);
        if (!cancelled) {
          notifications.show({
            title: "Failed to load job details",
            message: String(err?.response?.data?.message || err?.message || "Unknown error"),
            color: "red",
            icon: <IconX size={20} />,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [jobId, axiosCfg, navigate]);

  const handleApply = () => {
    if (!jobId) return;
    navigate(`/apply/${jobId}`);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        <div className="animate-pulse space-y-3">
          <div className="h-6 w-64 bg-gray-200 rounded" />
          <div className="h-4 w-40 bg-gray-200 rounded" />
          <div className="h-20 w-full bg-gray-100 rounded" />
          <div className="h-10 w-32 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        <p className="font-MTNfont text-red-600">Job not found.</p>
      </div>
    );
  }

  const isExpired = (() => {
    if (!job.applicationDeadline) return false;
    const d = new Date(job.applicationDeadline);
    if (Number.isNaN(d.getTime())) return false;
    return d < new Date();
  })();

  // ---------- UI ----------
  return (
    // Make the page itself scrollable & give bottom padding so the sticky button doesn't overlap the last card
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-5 min-h-screen overflow-y-auto pb-24">
      {/* Title row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-MTNfont font-bold text-[18px] md:text-[20px] text-gray-900">
          {job.positionTitle}
        </h1>

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleApply}
            disabled={isExpired}
            className={`px-4 py-2 rounded font-MTNfont text-sm ${
              isExpired
                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                : "bg-[#FFCC00] text-black hover:bg-yellow-400"
            }`}
            title={isExpired ? "Application deadline has passed" : "Apply for this job"}
          >
            {isExpired ? "Closed" : "Apply Now"}
          </button>

          {/* (optional) copy/share link */}
          <button
            onClick={() => {
              try {
                navigator.clipboard?.writeText(window.location.href);
                notifications.show({ title: "Link copied", message: "Job link copied to clipboard", color: "teal" });
              } catch (_) {}
            }}
            className="px-3 py-2 rounded font-MTNfont text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            Share
          </button>
        </div>
      </div>

      {/* Meta card: clean, compact, professional */}
      <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetaItem label="Reference No." value={job.referenceNumber} />
          <MetaItem label="Department" value={job.department} />
          <MetaItem label="Reports to:" value={job.level} />
          <MetaItem label="Location" value={[job.city, job.region].filter(Boolean).join(", ")} />
          <MetaItem label="Posted" value={formatDate(job.createdAt)} />
          <MetaItem label="Deadline" value={formatDate(job.applicationDeadline)} />
          <MetaItem label="Status" value={isExpired ? "Closed" : "Open"} />
        </div>
      </div>

      {/* Details */}
      <div className="mt-4">
        <JobDetailsAccordion
          aboutMtn={job.aboutMtn}
          mission={job.mission}
          keyPerformanceAreas={job.keyPerformanceAreas}
          experience={job.experience}
          educationBackground={job.educationBackground}
          applyInstructions={job.applyInstructions}
        />
      </div>

      {/* Sticky apply on mobile for “full” experience */}
      <div className="fixed inset-x-0 bottom-3 flex justify-center md:hidden pointer-events-none">
        <button
          onClick={handleApply}
          disabled={isExpired}
          className={`pointer-events-auto px-5 py-2 rounded-full shadow-md font-MTNfont text-sm ${
            isExpired
              ? "bg-gray-300 text-gray-600 cursor-not-allowed"
              : "bg-[#FFCC00] text-black"
          }`}
        >
          {isExpired ? "Closed" : "Apply Now"}
        </button>
      </div>

      {/* focus ring / scroll guarantees */}
      <style>{`
        :where(button, a, input, select, textarea):focus {
          outline: 2px solid #FFCC00;
          box-shadow: 0 0 0 3px rgba(255, 204, 0, 0.35);
          border-color: #FFCC00 !important;
        }
        /* Make sure root can scroll (handles cases where a parent used h-screen/overflow-hidden) */
        html, body, #root { height: auto !important; overflow-y: auto !important; }
      `}</style>
    </div>
  );
};

export default JobDetailsPage;
